import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Rating,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { elevatedPanelSx } from '../../constants/shape';
import StatusChip from '../../components/StatusChip';

const QualityControl: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState<any>(null);
  const [formData, setFormData] = useState({
    processType: '',
    batchNumber: '',
    inspector: '',
    inspectionDate: '',
    qualityScore: 0,
    contaminationLevel: '',
    moistureContent: '',
    processingAccuracy: '',
    status: 'Pending',
    notes: '',
  });

  // Mock data for demonstration
  const qualityChecks = [
    {
      id: '1',
      processType: 'Plastic Sorting',
      batchNumber: 'PS-2024-001',
      inspector: 'John Smith',
      inspectionDate: '2024-03-20',
      qualityScore: 4.5,
      contaminationLevel: '2%',
      moistureContent: '0.5%',
      processingAccuracy: '98%',
      status: 'Passed',
      notes: 'Excellent processing quality',
    },
    // Add more mock quality checks as needed
  ];

  const processTypes = [
    'Plastic Sorting',
    'Paper Processing',
    'Metal Separation',
    'Glass Cleaning',
    'Organic Composting',
    'Hazardous Treatment',
  ];

  const handleOpen = (quality?: any) => {
    if (quality) {
      setSelectedQuality(quality);
      setFormData({
        processType: quality.processType,
        batchNumber: quality.batchNumber,
        inspector: quality.inspector,
        inspectionDate: quality.inspectionDate,
        qualityScore: quality.qualityScore,
        contaminationLevel: quality.contaminationLevel,
        moistureContent: quality.moistureContent,
        processingAccuracy: quality.processingAccuracy,
        status: quality.status,
        notes: quality.notes,
      });
    } else {
      setSelectedQuality(null);
      setFormData({
        processType: '',
        batchNumber: '',
        inspector: '',
        inspectionDate: '',
        qualityScore: 0,
        contaminationLevel: '',
        moistureContent: '',
        processingAccuracy: '',
        status: 'Pending',
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQuality(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    handleClose();
  };

  return (
    <Box>
      <PageHeader
        icon={<FactCheckIcon />}
        title={t('processing.quality.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              borderRadius: '12px',
              px: 3,
              py: 1.5,
            }}
          >
            {t('processing.quality.newQualityCheck')}
          </Button>
        }
      />

      <Paper elevation={2} sx={elevatedPanelSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.process')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.batch')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.inspector')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.date')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.qualityScore')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.contamination')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.moisture')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.processingAccuracy')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qualityChecks.map((check) => (
                <TableRow 
                  key={check.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {check.processType}
                    </Typography>
                  </TableCell>
                  <TableCell>{check.batchNumber}</TableCell>
                  <TableCell>{check.inspector}</TableCell>
                  <TableCell>{check.inspectionDate}</TableCell>
                  <TableCell>
                    <Rating 
                      value={check.qualityScore} 
                      precision={0.5} 
                      readOnly 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{check.contaminationLevel}</TableCell>
                  <TableCell>{check.moistureContent}</TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={parseInt(check.processingAccuracy)} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {check.processingAccuracy}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={check.status}
                      value={check.status}
                      icon={
                        check.status === 'Passed' ? <CheckCircleIcon /> :
                        check.status === 'Warning' ? <WarningIcon /> :
                        <ErrorIcon />
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => handleOpen(check)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "16px 16px 0 0",
        }}>
          {selectedQuality ? t('processing.quality.editDialogTitle') : t('processing.quality.addDialogTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('processing.quality.processType')}</InputLabel>
                  <Select
                    value={formData.processType}
                    onChange={(e) => setFormData({ ...formData, processType: e.target.value })}
                    label={t('processing.quality.processType')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    {processTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.batchNumber')}
                  value={formData.batchNumber}
                  onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.inspector')}
                  value={formData.inspector}
                  onChange={(e) => setFormData({ ...formData, inspector: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.inspectionDate')}
                  type="date"
                  value={formData.inspectionDate}
                  onChange={(e) => setFormData({ ...formData, inspectionDate: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography component="legend">{t('processing.quality.qualityScore')}</Typography>
                <Rating
                  value={formData.qualityScore}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, qualityScore: newValue || 0 });
                  }}
                  precision={0.5}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.contaminationLevel')}
                  value={formData.contaminationLevel}
                  onChange={(e) => setFormData({ ...formData, contaminationLevel: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.quality.contaminationPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.moistureContent')}
                  value={formData.moistureContent}
                  onChange={(e) => setFormData({ ...formData, moistureContent: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.quality.moisturePlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.processingAccuracy')}
                  value={formData.processingAccuracy}
                  onChange={(e) => setFormData({ ...formData, processingAccuracy: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.quality.accuracyPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label={t('common.status')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    <MenuItem value="Pending">{t('processing.quality.statusPending')}</MenuItem>
                    <MenuItem value="Passed">{t('processing.quality.statusPassed')}</MenuItem>
                    <MenuItem value="Warning">{t('processing.quality.statusWarning')}</MenuItem>
                    <MenuItem value="Failed">{t('processing.quality.statusFailed')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {selectedQuality ? t('common.update') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default QualityControl; 